import {
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {IDropdownSettings, ListItem, MS_ITEM_SEPERATOR_CLASS} from './multiselect.model';
import {ListFilterPipe} from './list-filter.pipe';

declare let $: any;

export const DROPDOWN_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MultiSelectComponent),
  multi: true
};
const noop = () => {
};


@Component({
    selector: 'ng-multiselect-custom',
    templateUrl: './multi-select.component.html',
    styleUrls: ['./multi-select.component.scss'],
    providers: [DROPDOWN_CONTROL_VALUE_ACCESSOR],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MultiSelectComponent implements ControlValueAccessor {
  @Input()
  searchText: string = '';
  MS_ITEM_SEPERATOR_CLASS = MS_ITEM_SEPERATOR_CLASS;
  @ViewChild('multiSelectDrop') multiSelectDrop!: ElementRef;

  public _settings!: IDropdownSettings;
  public _data: Array<ListItem> = [];
  public selectedItems: Array<ListItem> = [];
  public isDropdownOpen = true;
  _placeholder = 'Select';
  filter: ListItem = new ListItem(this.data);
  defaultSettings: IDropdownSettings = {
    singleSelection: false,
    idField: 'id',
    textField: 'text',
    enableCheckAll: true,
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    allowSearchFilter: false,
    limitSelection: -1,
    clearSearchFilter: true,
    maxHeight: 197,
    itemsShowLimit: 999999999999,
    searchPlaceholderText: 'Search',
    noDataAvailablePlaceholderText: 'No data available',
    closeDropDownOnSelection: false,
    showSelectedItemsAtTop: false,
    defaultOpen: false,
    matchItemDropWidthToControl: false,
    resetTextValueOnSelection: false,
    allowWildFilter: false,
    allowAdvancedWildcardSearch: false
  };
  selectedLanguageCode: any;
  defaultItemDropWidth = '100%';

  @Input()
  public set placeholder(value: string) {
    if (value) {
      this._placeholder = value;
    } else {
      this._placeholder = 'Select';
    }
  }

  @Input()
  disabled = false;

  @Input()
  public set settings(value: IDropdownSettings) {
    if (value) {
      this._settings = Object.assign(this.defaultSettings, value);
    } else {
      this._settings = Object.assign(this.defaultSettings);
    }
  }

  @Input()
  public set data(value: Array<any>) {
    if (!value) {
      this._data = [];
    } else if (this._settings) {
      const idField = this._settings.idField ? this._settings.idField : '';
      const textField = this._settings.textField ? this._settings.textField : '';
      const classField = this._settings.classField ? this._settings.classField : '';
      this._data = value.map(
        (item: any) =>
          typeof item === 'string'
            ? new ListItem(item)
            : new ListItem({
              id: item[idField],
              text: item[textField],
              class: item[classField]
            })
      );
    }
  }

  @ViewChild('input1') inputEl!: ElementRef;

  @Output('onFilterChange')
  onFilterChange: EventEmitter<ListItem> = new EventEmitter<any>();

  @Output('onDropDownClose')
  onDropDownClose: EventEmitter<ListItem> = new EventEmitter<any>();

  @Output('onSelect')
  onSelect: EventEmitter<ListItem> = new EventEmitter<any>();

  @Output('onDeSelect')
  onDeSelect: EventEmitter<ListItem> = new EventEmitter<any>();

  @Output('onSelectAll')
  onSelectAll: EventEmitter<Array<ListItem>> = new EventEmitter<Array<any>>();

  @Output('onDeSelectAll')
  onDeSelectAll: EventEmitter<Array<ListItem>> = new EventEmitter<Array<any>>();

  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: any) => void = noop;

  onFilterTextChange($event: any) {
    this.onFilterChange.emit($event);
  }

  constructor(private pipe: ListFilterPipe, private changeDetectorRef: ChangeDetectorRef,
             ) {
    this.setPlaceholders();
  
  }

  onItemClick($event: any, item: ListItem) {
    if (this.disabled) {
      return false;
    }

    const found = this.isSelected(item);
    const limitSelection = this._settings.limitSelection ? this._settings.limitSelection : 0;
    const allowAdd =
      this._settings.limitSelection === -1 ||
      (limitSelection > 0 &&
        this.selectedItems.length < limitSelection);
    if (!found) {
      if (allowAdd) {
        this.addSelected(item);
      }
    } else {
      this.removeSelected(item);
    }
    if (
      this._settings.singleSelection &&
      this._settings.closeDropDownOnSelection
    ) {
      this.closeDropdown();
    }
    return true;
  }

  writeValue(value: any) {

    const idField = this._settings.idField ? this._settings.idField : '';
    const textField = this._settings.textField ? this._settings.textField : '';
    const classField = this._settings.classField ? this._settings.classField : '';
    const limitSel = this._settings.limitSelection ? this._settings.limitSelection : 0;

    if (value !== undefined && value !== null && value.length > 0) {
      if (this._settings.singleSelection) {
        try {
          if (value.length >= 1) {
            const firstItem = value[0];
            this.selectedItems = [
              typeof firstItem === 'string'
                ? new ListItem(firstItem)
                : new ListItem({
                  id: firstItem[idField],
                  text: firstItem[textField]
                })
            ];
          }
        } catch (e) {
          // console.error(e.body.msg);
        }
      } else {
        const _data = value.map(
          (item: any) =>
            typeof item === 'string'
              ? new ListItem(item)
              : new ListItem({
                id: item[idField],
                text: item[textField]
              })
        );
        if (limitSel > 0) {
          this.selectedItems = _data.splice(0, this._settings.limitSelection);
        } else {
          this.selectedItems = _data;
        }
      }
    } else {
      this.selectedItems = [];
    }
    this.onChangeCallback(value);
  }

  // From ControlValueAccessor interface
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  // From ControlValueAccessor interface
  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  // Set touched on blur
  @HostListener('blur')
  public onTouched() {
    this.closeDropdown();
    this.onTouchedCallback();
  }

  trackByFn(index: any, item: any) {
    return item.id;
  }

  isSelected(clickedItem: ListItem) {
    let found = false;
    // this.inputEl.nativeElement.focus();
    this.selectedItems.forEach(item => {
      if (clickedItem.id === item.id) {
        found = true;
      }
    });
    return found;
  }

  isLimitSelectionReached(): boolean {
    return this._settings.limitSelection === this.selectedItems.length;
  }

  isAllItemsSelected(): boolean {
    return this._data.length === this.selectedItems.length;
  }

  showButton(): boolean {
    const limitSel = this._settings.limitSelection ? this._settings.limitSelection : 0;
    if (!this._settings.singleSelection) {
      if (limitSel > 0) {
        return false;
      }
      // this._settings.enableCheckAll = this._settings.limitSelection === -1 ? true : false;
      return true; // !this._settings.singleSelection && this._settings.enableCheckAll && this._data.length > 0;
    } else {
      // should be disabled in single selection mode
      return false;
    }
  }

  itemShowRemaining(): number {
    const showLimit = this._settings.itemsShowLimit ? this._settings.itemsShowLimit : 0;
    return this.selectedItems.length - showLimit;
  }

  addSelected(item: ListItem) {
    if (this._settings.singleSelection) {
      this.selectedItems = [];
      this.selectedItems.push(item);
    } else {
      this.selectedItems.push(item);
    }
    this.onChangeCallback(this.emittedValue(this.selectedItems));
    this.onSelect.emit(this.emittedValue(item));
    if (this._settings.resetTextValueOnSelection) {
      this.removeSelected(item);
    }
  }

  removeSelected(itemSel: ListItem) {
    this.selectedItems.forEach(item => {
      if (itemSel.id === item.id) {
        this.selectedItems.splice(this.selectedItems.indexOf(item), 1);
      }
    });
    this.onChangeCallback(this.emittedValue(this.selectedItems));
    this.onDeSelect.emit(this.emittedValue(itemSel));
  }

  emittedValue(val: any): any {
    const selected: Array<any> = [];
    if (Array.isArray(val)) {
      val.map(item => {
        if (item.id === item.text) {
          selected.push(item.text);
        } else {
          selected.push(this.objectify(item));
        }
      });
    } else {
      if (val) {
        if (val.id === val.text) {
          return val.text;
        } else {
          return this.objectify(val);
        }
      }
    }
    return selected;
  }

  objectify(val: ListItem) {
    const obj: any = {};
    const idField = this._settings.idField ? this._settings.idField : '';
    const textField = this._settings.textField ? this._settings.textField : '';

    obj[idField] = val.id;
    obj[textField] = val.text;
    return obj;
  }

  toggleDropdown(evt: any) {
    if (this._settings.matchItemDropWidthToControl && this.defaultItemDropWidth !== 'auto') {
      this.defaultItemDropWidth = this.multiSelectDrop.nativeElement.offsetWidth + 'px';
    }
    evt.preventDefault();
    if (this.disabled && this._settings.singleSelection) {
      return;
    }
    this._settings.defaultOpen = !this._settings.defaultOpen;
    if (!this._settings.defaultOpen) {
      this.onDropDownClose.emit();
    } else {
    // Pre-fill search field with searchText when opening
    if (this.searchText && this.searchText.trim().length > 0) {
      this.filter.text = this.searchText;
      this.onFilterTextChange(this.searchText);
    }
      setTimeout(() => {
        const parentWrapper = $(evt.srcElement).closest('.multiselect-dropdown');
        const searchInput = parentWrapper.find('.filter-textbox input[type=text]');

        if (searchInput.length) {
          searchInput.focus();
        }
      }, 200);

    }
  }

  closeDropdown() {
    this._settings.defaultOpen = false;
    // clear search text
    if (this._settings.clearSearchFilter) {
      this.filter.text = '';
    }
    this.onDropDownClose.emit();
  }

  toggleSelectAll() {
    if (this.disabled) {
      return false;
    }
    if (!this.isAllItemsSelected()) {
      this.selectedItems = this._data.slice();
      this.onSelectAll.emit(this.emittedValue(this.selectedItems));
    } else {
      this.selectedItems = [];
      this.onDeSelectAll.emit(this.emittedValue(this.selectedItems));
    }
    this.onChangeCallback(this.emittedValue(this.selectedItems));
    return true;
  }

  onEnterPress(event: any) {
    if (event.keyCode === 13) {
      $('ul.item2 li:visible').first().trigger('click');
    }
  }

  refresh() {
    setTimeout(() => {
      this.changeDetectorRef.detectChanges();
    }, 50);
  }

  private setPlaceholders() {
    if (this.selectedLanguageCode === 'no') {
      this._placeholder = 'Velg';
      this.defaultSettings.searchPlaceholderText = 'Søk';
      this.defaultSettings.selectAllText = 'Velg alle';
      this.defaultSettings.unSelectAllText = 'Fjern alle';
    } else {
      this._placeholder = 'Select';
      this.defaultSettings.searchPlaceholderText = 'Search';
      this.defaultSettings.selectAllText = 'Select All';
      this.defaultSettings.unSelectAllText = 'UnSelect All';
    }
  }
}
